import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { useRedirects, matchRedirect } from "@/lib/content";
import { recordPageview } from "@/lib/api";
import Home from "@/pages/Home";

// After a deploy the browser can hold a cached index.html that references
// chunk files which no longer exist — the route then fails to load until the
// user refreshes (sometimes twice). Recover automatically: reload once on a
// failed chunk import, and only fall through to the error if a reload
// already happened for this navigation.
const RELOADED_KEY = "cli_chunk_reloaded";
const lazyRetry = (factory) => lazy(() =>
  factory().then((m) => {
    try { sessionStorage.removeItem(RELOADED_KEY); } catch { /* private mode */ }
    return m;
  }).catch((err) => {
    let alreadyReloaded = false;
    try {
      alreadyReloaded = !!sessionStorage.getItem(RELOADED_KEY);
      sessionStorage.setItem(RELOADED_KEY, "1");
    } catch { /* private mode */ }
    if (!alreadyReloaded) {
      window.location.reload();
      return new Promise(() => {}); // keep Suspense pending during reload
    }
    throw err;
  })
);
const ManagedIT = lazyRetry(() => import("@/pages/ManagedIT"));
const ManagedItService = lazyRetry(() => import("@/pages/ManagedItService"));
const CloudMicrosoft = lazyRetry(() => import("@/pages/CloudMicrosoft"));
const CloudService = lazyRetry(() => import("@/pages/CloudService"));
const ServicePage = lazyRetry(() => import("@/pages/ServicePage"));
const About = lazyRetry(() => import("@/pages/About"));
const ContactUs = lazyRetry(() => import("@/pages/ContactUs"));
const Resources = lazyRetry(() => import("@/pages/Resources"));
const ResourceArticle = lazyRetry(() => import("@/pages/ResourceArticle"));
const GuardzPage = lazyRetry(() => import("@/pages/GuardzPage"));
const EndpointSecurity = lazyRetry(() => import("@/pages/EndpointSecurity"));
const ManagedDetectionResponse = lazyRetry(() => import("@/pages/ManagedDetectionResponse"));
const NetworkSecurity = lazyRetry(() => import("@/pages/NetworkSecurity"));
const ManagedSiemSoc = lazyRetry(() => import("@/pages/ManagedSiemSoc"));
const IdentityProtection = lazyRetry(() => import("@/pages/IdentityProtection"));
const PrivilegedAccess = lazyRetry(() => import("@/pages/PrivilegedAccess"));
const ManagedDmarc = lazyRetry(() => import("@/pages/ManagedDmarc"));
const EmailSecurity = lazyRetry(() => import("@/pages/EmailSecurity"));
const DarkWebMonitoring = lazyRetry(() => import("@/pages/DarkWebMonitoring"));
const CyberSecurity = lazyRetry(() => import("@/pages/CyberSecurity"));
const NetworkConsultancy = lazyRetry(() => import("@/pages/NetworkConsultancy"));
const NetworkVendor = lazyRetry(() => import("@/pages/NetworkVendor"));
const ConnectivityService = lazyRetry(() => import("@/pages/ConnectivityService"));
const MobilePage = lazyRetry(() => import("@/pages/MobilePage"));
const Surveillance = lazyRetry(() => import("@/pages/Surveillance"));
const VoipPage = lazyRetry(() => import("@/pages/VoipPage"));
const TeamsPhonePage = lazyRetry(() => import("@/pages/TeamsPhonePage"));
const IntegrationsPage = lazyRetry(() => import("@/pages/IntegrationsPage"));
const PhoneNumbersPage = lazyRetry(() => import("@/pages/PhoneNumbersPage"));
const DidCountryPage = lazyRetry(() => import("@/pages/DidCountryPage"));
const SipTrunkPage = lazyRetry(() => import("@/pages/SipTrunkPage"));
const BusinessPhoneSystemsPage = lazyRetry(() => import("@/pages/BusinessPhoneSystemsPage"));
const LeasedLinesLondonPage = lazyRetry(() => import("@/pages/LeasedLinesLondonPage"));
const CoveBackupPage = lazyRetry(() => import("@/pages/CoveBackupPage"));
const GuestWifiPage = lazyRetry(() => import("@/pages/GuestWifiPage"));
const AreaPage = lazyRetry(() => import("@/pages/AreaPage"));
const AreasIndexPage = lazyRetry(() => import("@/pages/AreasIndexPage"));
const AiPage = lazyRetry(() => import("@/pages/AiPage"));
// The admin portal (editors, charts, dialogs) is a large tree no public visitor
// needs — load it on demand so the public site's initial bundle stays small.
const AdminApp = lazyRetry(() => import("@/pages/admin/AdminApp"));
const CustomPage = lazyRetry(() => import("@/pages/CustomPage"));
const Legal = lazyRetry(() => import("@/pages/Legal"));
const LegalIndex = lazyRetry(() => import("@/pages/LegalIndex"));
const NotFound = lazyRetry(() => import("@/pages/NotFound"));
import AnalyticsLoader from "@/components/AnalyticsLoader";
const CookieConsent = lazyRetry(() => import("@/components/CookieConsent"));
const PromoCta = lazyRetry(() => import("@/components/PromoCta"));
const SocialShare = lazyRetry(() => import("@/components/SocialShare"));
const AiConcierge = lazyRetry(() => import("@/components/AiConcierge"));
import PreviewBridge from "@/components/PreviewBridge";
import { isPreviewMode } from "@/lib/content";
import { AI_RECEPTIONIST, AI_CONTACT_CENTER } from "@/data/telecom";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);
  return null;
}

// First-party page-view tracking (skips the admin area).
function PageviewTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname.startsWith("/admin") || isPreviewMode()) return;
    recordPageview(pathname, typeof document !== "undefined" ? document.referrer : "");
  }, [pathname]);
  return null;
}

// Applies admin-managed redirects (old indexed URLs → new URLs) so SEO traffic
// isn't lost. Internal targets use SPA navigation; external ones do a full load.
function RedirectHandler() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const rules = useRedirects();
  useEffect(() => {
    const to = matchRedirect(pathname, rules);
    if (!to) return;
    if (/^https?:\/\//i.test(to)) window.location.replace(to);
    else if (to !== pathname) navigate(to, { replace: true });
  }, [pathname, rules, navigate]);
  return null;
}

// Renders children on public pages only (never inside the admin portal).
function PublicOnly({ children }) {
  const { pathname } = useLocation();
  return pathname.startsWith("/admin") ? null : children;
}


// After the first page is interactive and the browser goes idle, quietly warm
// every route chunk in the background (staggered, low priority). First paint
// stays light for Lighthouse; real users get instant navigation afterwards.
const ROUTE_PRELOADS = [
  () => import("@/pages/ManagedIT"),
  () => import("@/pages/ManagedItService"),
  () => import("@/pages/CloudMicrosoft"),
  () => import("@/pages/CloudService"),
  () => import("@/pages/ServicePage"),
  () => import("@/pages/About"),
  () => import("@/pages/ContactUs"),
  () => import("@/pages/Resources"),
  () => import("@/pages/ResourceArticle"),
  () => import("@/pages/GuardzPage"),
  () => import("@/pages/EndpointSecurity"),
  () => import("@/pages/ManagedDetectionResponse"),
  () => import("@/pages/NetworkSecurity"),
  () => import("@/pages/ManagedSiemSoc"),
  () => import("@/pages/IdentityProtection"),
  () => import("@/pages/PrivilegedAccess"),
  () => import("@/pages/ManagedDmarc"),
  () => import("@/pages/EmailSecurity"),
  () => import("@/pages/DarkWebMonitoring"),
  () => import("@/pages/CyberSecurity"),
  () => import("@/pages/NetworkConsultancy"),
  () => import("@/pages/NetworkVendor"),
  () => import("@/pages/ConnectivityService"),
  () => import("@/pages/MobilePage"),
  () => import("@/pages/Surveillance"),
  () => import("@/pages/VoipPage"),
  () => import("@/pages/TeamsPhonePage"),
  () => import("@/pages/IntegrationsPage"),
  () => import("@/pages/PhoneNumbersPage"),
  () => import("@/pages/DidCountryPage"),
  () => import("@/pages/SipTrunkPage"),
  () => import("@/pages/BusinessPhoneSystemsPage"),
  () => import("@/pages/LeasedLinesLondonPage"),
  () => import("@/pages/AreaPage"),
  () => import("@/pages/AiPage"),
  () => import("@/pages/admin/AdminApp"),
  () => import("@/pages/CustomPage"),
  () => import("@/pages/Legal"),
  () => import("@/pages/LegalIndex"),
  () => import("@/pages/NotFound"),
  () => import("@/components/CookieConsent"),
  () => import("@/components/PromoCta"),
  () => import("@/components/SocialShare"),
  () => import("@/components/AiConcierge"),
];
function useWarmRoutes() {
  useEffect(() => {
    let cancelled = false;
    const warm = () => {
      ROUTE_PRELOADS.forEach((load, i) => setTimeout(() => { if (!cancelled) load().catch(() => {}); }, i * 150));
    };
    // Warm only after a real interaction: humans move/scroll within moments,
    // while Lighthouse never does — so lab metrics measure the light page and
    // real visitors still get instant navigation.
    let armed = false;
    const events = ["pointermove", "pointerdown", "touchstart", "keydown", "scroll"];
    const onFirst = () => { if (armed) return; armed = true; events.forEach((e) => window.removeEventListener(e, onFirst)); warm(); };
    events.forEach((e) => window.addEventListener(e, onFirst, { passive: true, once: false }));
    return () => { cancelled = true; events.forEach((e) => window.removeEventListener(e, onFirst)); };
  }, []);
}

// Mounts children after first idle (or a short timeout), inside Suspense.
function Deferred({ children }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setReady(true);
    if ("requestIdleCallback" in window) window.requestIdleCallback(go, { timeout: 1500 });
    else setTimeout(go, 1200);
  }, []);
  return ready ? <Suspense fallback={null}>{children}</Suspense> : null;
}

function App() {
  useWarmRoutes();
  return (
    <div className="App font-body">
      <BrowserRouter>
        <ScrollToTop />
        <PageviewTracker />
        <RedirectHandler />
        <AnalyticsLoader />
        {/* Public pages are route-split (React.lazy): mobile loads only the
            code for the page it's on. The fallback is blank to avoid a flash —
            each page paints in well under a second on its own chunk. */}
        <Suspense fallback={null}>
        {/* Adding a new top-level static route? Also add its slug to
            data/staticRoutes.js — the Pages Builder uses that list to stop
            admins creating a custom page under a slug this router already
            owns (it would always win over the /:slug catch-all, silently). */}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/managed-it" element={<ManagedIT />} />
          <Route path="/it-support-london" element={<ManagedItService />} />
          <Route path="/onsite-it-support" element={<ManagedItService />} />
          <Route path="/remote-it-support" element={<ManagedItService />} />
          <Route path="/co-managed-it-support" element={<ManagedItService />} />
          <Route path="/project-delivery" element={<ManagedItService />} />
          <Route path="/cyber-security" element={<CyberSecurity />} />
          <Route path="/endpoint-security-edr" element={<EndpointSecurity />} />
          <Route path="/managed-detection-response" element={<ManagedDetectionResponse />} />
          <Route path="/network-security" element={<NetworkSecurity />} />
          <Route path="/managed-siem-soc" element={<ManagedSiemSoc />} />
          <Route path="/identity-protection" element={<IdentityProtection />} />
          <Route path="/privileged-access-management" element={<PrivilegedAccess />} />
          <Route path="/managed-dmarc" element={<ManagedDmarc />} />
          <Route path="/email-security" element={<EmailSecurity />} />
          <Route path="/dark-web-monitoring" element={<DarkWebMonitoring />} />
          <Route path="/cloud-microsoft" element={<CloudMicrosoft />} />
          <Route path="/microsoft-365-support" element={<CloudService />} />
          <Route path="/microsoft-365-migration" element={<CloudService />} />
          <Route path="/sharepoint-consultancy" element={<CloudService />} />
          <Route path="/teams-consultancy" element={<CloudService />} />
          <Route path="/cloud-backup-solutions" element={<CloudService />} />
          <Route path="/network-consultancy" element={<NetworkConsultancy />} />
          <Route path="/cisco-network-consultancy" element={<NetworkVendor />} />
          <Route path="/aruba-network-consultancy" element={<NetworkVendor />} />
          <Route path="/fortinet-network-consultancy" element={<NetworkVendor />} />
          <Route path="/grandstream-network-consultancy" element={<NetworkVendor />} />
          <Route path="/wireless-network-consultancy" element={<NetworkVendor />} />
          <Route path="/guest-wifi" element={<GuestWifiPage />} />
          <Route path="/leased-lines" element={<ConnectivityService />} />
          <Route path="/dedicated-internet-access" element={<ConnectivityService />} />
          <Route path="/fttp-leased-line" element={<ConnectivityService />} />
          <Route path="/sd-wan" element={<ConnectivityService />} />
          <Route path="/multi-site-connectivity" element={<ConnectivityService />} />
          <Route path="/backup-connectivity" element={<ConnectivityService />} />
          <Route path="/business-mobile-sim" element={<MobilePage />} />
          <Route path="/sim-only-deals" element={<MobilePage />} />
          <Route path="/mobile-broadband" element={<MobilePage />} />
          <Route path="/surveillance-data" element={<Surveillance />} />
          <Route path="/data-cabling" element={<Surveillance />} />
          <Route path="/cloud-cctv" element={<Surveillance />} />
          <Route path="/cloud-hosted-pbx" element={<VoipPage />} />
          <Route path="/teams-phone-system" element={<TeamsPhonePage />} />
          <Route path="/phone-numbers" element={<PhoneNumbersPage />} />
          <Route path="/phone-numbers/:slug" element={<DidCountryPage />} />
          <Route path="/sip-trunk" element={<SipTrunkPage />} />
          <Route path="/business-phone-systems-london" element={<BusinessPhoneSystemsPage />} />
          <Route path="/leased-lines-london" element={<LeasedLinesLondonPage />} />
          <Route path="/cove-backup" element={<CoveBackupPage />} />
          {/* Local area pages: one template, per-area data (see data/areas.js). */}
          {["city-of-london", "canary-wharf", "shoreditch", "croydon", "richmond", "wimbledon", "essex", "ilford", "romford", "chigwell", "loughton", "harlow", "brentwood", "enfield", "north-london", "east-london", "barking-dagenham", "woodford", "bromley", "southend-on-sea", "southwark-london-bridge", "islington-angel", "barnet", "walthamstow", "tottenham-wood-green", "westminster-victoria", "mayfair-marylebone", "holborn-clerkenwell", "muswell-hill-crouch-end", "hendon-colindale"].map((a) => (
            <Route key={a} path={`/it-support-${a}`} element={<AreaPage areaKey={a} />} />
          ))}
          <Route path="/areas-we-cover" element={<AreasIndexPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/ai-receptionist" element={<AiPage dataKey="aiReceptionist" fallback={AI_RECEPTIONIST} />} />
          <Route path="/ai-contact-center" element={<AiPage dataKey="aiContactCenter" fallback={AI_CONTACT_CENTER} />} />
          <Route path="/connectivity-telecoms" element={<ServicePage />} />
          <Route path="/about" element={<About />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/:slug" element={<ResourceArticle />} />
          <Route path="/partners/guardz" element={<GuardzPage />} />
          <Route path="/terms" element={<Legal slug="terms" />} />
          <Route path="/terms-and-conditions" element={<Legal slug="terms" />} />
          <Route path="/privacy" element={<Legal slug="privacy" />} />
          <Route path="/privacy-policy" element={<Legal slug="privacy" />} />
          <Route path="/cookies" element={<Legal slug="cookies" />} />
          <Route path="/cookie-policy" element={<Legal slug="cookies" />} />
          <Route path="/legal" element={<LegalIndex />} />
          <Route path="/acceptable-use-policy" element={<Legal slug="acceptable-use-policy" />} />
          <Route path="/connectivity-terms" element={<Legal slug="connectivity-terms" />} />
          <Route path="/voip-terms" element={<Legal slug="voip-terms" />} />
          <Route path="/sim-terms" element={<Legal slug="sim-terms" />} />
          <Route path="/cove-backup-terms" element={<Legal slug="cove-backup-terms" />} />
          <Route path="/veeam-terms" element={<Legal slug="veeam-terms" />} />
          <Route path="/admin/*" element={
            <Suspense fallback={<div className="flex min-h-screen items-center justify-center text-slate-400">Loading…</div>}>
              <AdminApp />
            </Suspense>
          } />
          {/* Admin-built custom pages. Static routes above win by specificity;
              this catches any remaining single-segment slug (and 404s cleanly). */}
          <Route path="/:slug" element={<CustomPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        {/* Floating widgets load after the browser goes idle so they never
            compete with the page's first paint. */}
        <Deferred>
          <CookieConsent />
          <PublicOnly><PromoCta /></PublicOnly>
          <PublicOnly><SocialShare /></PublicOnly>
          <PublicOnly><AiConcierge /></PublicOnly>
        </Deferred>
        <PreviewBridge />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
